import { useState, type ReactElement } from 'react';
import { KeyRound, Plus } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DataTableHeaderRow,
  DataTableTable,
  DataTableTbody,
  DataTableTd,
  DataTableTh,
  DataTableThead,
  DataTableTr,
} from '@/components/ui/data-table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/shell/PageHeader';
import { toast } from '@/hooks/useToast';

import {
  useClusterGrants,
  useCreateClusterGrant,
  useRevokeClusterGrant,
  useTenants,
  type ClusterGrantViewModel,
} from '@/features/platform/api';
import { CreateGrantDialog } from '@/features/platform/components/CreateGrantDialog';
import type { ClusterGrantFilters } from '@/features/platform/endpoints';

export function PlatformGrantsPage(): ReactElement {
  const { t } = useTranslation();
  const [filters, setFilters] = useState<ClusterGrantFilters>({});
  const [createOpen, setCreateOpen] = useState(false);
  const [revoking, setRevoking] = useState<ClusterGrantViewModel | null>(null);

  const grantsQuery = useClusterGrants(filters);
  const tenantsQuery = useTenants();
  const createGrant = useCreateClusterGrant();
  const revokeGrant = useRevokeClusterGrant();

  const tenants = tenantsQuery.data?.tenants ?? [];
  const grants = grantsQuery.data ?? [];

  function handleRevoke(grant: ClusterGrantViewModel): void {
    revokeGrant.mutate(grant.id, {
      onSuccess: () => {
        setRevoking(null);
        revokeGrant.reset();
        toast({ title: t('routes.platform.grants.revoke.success'), variant: 'success' });
      },
      onError: (error) => {
        toast({
          title: t('routes.platform.grants.revoke.error', { code: getErrorCodeForUi(error) }),
          variant: 'destructive',
        });
      },
    });
  }

  return (
    <>
      <PageHeader
        icon={<KeyRound className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.platform.grants.heading')}
        subtitle={t('routes.platform.grants.subheading')}
        actions={
          <Button type="button" size="sm" onClick={() => { setCreateOpen(true); }}>
            <Plus className="mr-1 h-4 w-4" aria-hidden="true" />
            {t('routes.platform.grants.createAction')}
          </Button>
        }
      />
      <div className="space-y-4 bg-background px-8 py-5">
        <div className="flex flex-wrap items-end gap-3">
          <Input
            aria-label={t('routes.platform.grants.filters.user')}
            placeholder={t('routes.platform.grants.filters.user')}
            value={filters.globalUserGuid ?? ''}
            onChange={(e) => { setFilters((p) => ({ ...p, globalUserGuid: e.target.value })); }}
            className="h-9 w-64 font-mono"
          />
          <Select
            aria-label={t('routes.platform.grants.filters.tenant')}
            value={filters.tenantUuid ?? ''}
            onChange={(e) => { setFilters((p) => ({ ...p, tenantUuid: e.target.value })); }}
            className="w-56"
          >
            <option value="">{t('routes.platform.grants.filters.allTenants')}</option>
            {tenants.map((tenant) => (
              <option key={tenant.uuid} value={tenant.uuid}>
                {tenant.label.length > 0 ? `${tenant.label} (${tenant.name})` : tenant.name}
              </option>
            ))}
          </Select>
          <Button type="button" variant="ghost" size="sm" onClick={() => { setFilters({}); }}>
            {t('routes.platform.grants.filters.clear')}
          </Button>
        </div>

        {grantsQuery.isPending && (
          <div className="space-y-2" aria-hidden="true">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        )}

        {grantsQuery.isError && (
          <div className="max-w-xl">
            <FetchErrorCallout
              error={grantsQuery.error}
              heading={t('routes.platform.grants.error.heading')}
              message={t('routes.platform.grants.error.message')}
              codeLabel={t('routes.platform.grants.error.codeLabel')}
              onRetry={() => {
                grantsQuery.refetch().catch(() => undefined);
              }}
              retryLabel={t('routes.platform.grants.error.retry')}
            />
          </div>
        )}

        {grantsQuery.isSuccess && grants.length === 0 && (
          <p className="text-sm text-muted-foreground" role="status">
            {t('routes.platform.grants.empty')}
          </p>
        )}

        {grantsQuery.isSuccess && grants.length > 0 && (
          <DataTableTable aria-label={t('routes.platform.grants.listAriaLabel')}>
            <DataTableThead>
              <DataTableHeaderRow>
                <DataTableTh widthClassName="w-[22%]">
                  {t('routes.platform.grants.table.user')}
                </DataTableTh>
                <DataTableTh widthClassName="w-[18%]">
                  {t('routes.platform.grants.table.tenant')}
                </DataTableTh>
                <DataTableTh widthClassName="w-[14%]">
                  {t('routes.platform.grants.table.capability')}
                </DataTableTh>
                <DataTableTh widthClassName="w-[14%]">
                  {t('routes.platform.grants.table.grantedBy')}
                </DataTableTh>
                <DataTableTh widthClassName="w-[14%]">
                  {t('routes.platform.grants.table.status')}
                </DataTableTh>
                <DataTableTh widthClassName="w-[8%]" align="right">
                  <span className="sr-only">{t('routes.platform.grants.table.actions')}</span>
                </DataTableTh>
              </DataTableHeaderRow>
            </DataTableThead>
            <DataTableTbody>
              {grants.map((grant) => (
                <DataTableTr key={grant.id} className="even:bg-muted/40">
                  <DataTableTd className="font-mono text-[11px] text-foreground">
                    {grant.globalUserGuid}
                  </DataTableTd>
                  <DataTableTd dataOffset className="font-mono text-[12px]">
                    {grant.tenantName ?? grant.tenantUuid}
                  </DataTableTd>
                  <DataTableTd dataOffset>
                    <Badge variant="outline">{grant.capability}</Badge>
                  </DataTableTd>
                  <DataTableTd dataOffset className="text-muted-foreground">
                    {grant.grantedBy ?? ''}
                  </DataTableTd>
                  <DataTableTd dataOffset>
                    {grant.isRevoked && (
                      <Badge variant="destructive">{t('routes.platform.grants.revoked')}</Badge>
                    )}
                  </DataTableTd>
                  <DataTableTd align="right" dataOffset>
                    {!grant.isRevoked && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-7 text-destructive hover:text-destructive"
                        onClick={() => { setRevoking(grant); }}
                      >
                        {t('routes.platform.grants.revoke.action')}
                      </Button>
                    )}
                  </DataTableTd>
                </DataTableTr>
              ))}
            </DataTableTbody>
          </DataTableTable>
        )}
      </div>

      <CreateGrantDialog
        open={createOpen}
        onOpenChange={(next) => {
          setCreateOpen(next);
          if (!next) createGrant.reset();
        }}
        tenants={tenants}
        onSubmit={(input) => {
          createGrant.mutate(input, {
            onSuccess: () => {
              setCreateOpen(false);
              createGrant.reset();
            },
          });
        }}
        pending={createGrant.isPending}
        submitError={createGrant.error}
      />

      <Dialog
        open={revoking !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRevoking(null);
            revokeGrant.reset();
          }
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('routes.platform.grants.revoke.title')}</DialogTitle>
            <DialogDescription>
              {t('routes.platform.grants.revoke.description')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setRevoking(null);
                revokeGrant.reset();
              }}
              disabled={revokeGrant.isPending}
            >
              {t('routes.platform.grants.revoke.cancel')}
            </Button>
            <Button
              type="button"
              variant="destructive"
              loading={revokeGrant.isPending}
              onClick={() => {
                if (revoking !== null) handleRevoke(revoking);
              }}
            >
              {t('routes.platform.grants.revoke.confirm')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
